"use client"

import React, { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  X,
  Package,
  Tag,
  FileText,
  Archive,
  AlertTriangle, 
  BarChart4, 
  DollarSign, 
  Pencil, 
  Save, 
  Trash, 
  XCircle, 
  Loader2,
  ChevronRight,
  CheckCircle2
} from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Textarea } from "../../../components/ui/textarea"
import { Label } from "../../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../../components/ui/alert-dialog"
import { Badge } from "../../../components/ui/badge"
import { useToast } from "../../../components/ui/use-toast"
import { createInventoryItem, updateInventoryItem, deleteInventoryItem, generateSKU, getInventoryItems } from "../../../../ApiCalls/mantisFlowsInventory"

const units = [
  { value: "unit", label: "Unidad" },
  { value: "kg", label: "Kilogramo" },
  { value: "g", label: "Gramo" },
  { value: "l", label: "Litro" },
  { value: "ml", label: "Mililitro" },
  { value: "m", label: "Metro" },
  { value: "cm", label: "Centímetro" },
  { value: "piece", label: "Pieza" },
  { value: "box", label: "Caja" },
  { value: "package", label: "Paquete" }
]

const currencies = [
  { value: "COP", label: "COP" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" }
]

export default function InventoryModal({ item, mode, onClose, onEditMode, clientId, categories, onSave }) {
  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    description: "",
    category_id: 0,
    subcategory_id: null,
    current_stock: 0,
    unit: "unit",
    min_stock_threshold: 0,
    max_stock_threshold: 100,
    common_price: 0,
    currency: "COP",
    notes: ""
  })

  const [subcategories, setSubcategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isReadOnly, setIsReadOnly] = useState(mode === "view")
  const [activeTab, setActiveTab] = useState("general")
  const [generateSKULoading, setGenerateSKULoading] = useState(false)

  const { toast } = useToast()

  useEffect(() => {
    setIsReadOnly(mode === "view")
    if (item && mode !== "create") {
      loadItemDetails(item.id)
      } else {
      // Reset form for new item
      setFormData({
        name: "",
        sku: "",
        description: "",
        category_id: 0,
        subcategory_id: null,
        current_stock: 0,
        unit: "unit",
        min_stock_threshold: 0,
        max_stock_threshold: 100,
        common_price: 0,
        currency: "COP",
        notes: ""
      })
    }
  }, [item, mode])

  useEffect(() => {
    if (formData.category_id && categories) {
      const category = categories.find(cat => cat.id === Number(formData.category_id))
      setSubcategories(category?.subcategories || [])
      if (!category?.subcategories?.find(sub => sub.id === formData.subcategory_id)) {
        setFormData(prev => ({ ...prev, subcategory_id: null }))
      }
        } else {
      setSubcategories([])
      setFormData(prev => ({ ...prev, subcategory_id: null }))
        }
  }, [formData.category_id, categories])

  const loadItemDetails = async (itemId) => {
    try {
      setLoading(true)
      const response = await getInventoryItems(itemId)

      if (response.success && response.data) {
        const itemData = response.data
        setFormData({
          name: itemData.name || "",
          sku: itemData.sku || "",
          description: itemData.description || "",
          category_id: itemData.category_id || 0,
          subcategory_id: itemData.subcategory_id || null,
          current_stock: itemData.current_stock || 0,
          unit: itemData.unit || "unit",
          min_stock_threshold: itemData.min_stock_threshold || 0,
          max_stock_threshold: itemData.max_stock_threshold || 100,
          common_price: itemData.common_price || 0,
          currency: itemData.currency || "COP",
          notes: itemData.notes || ""
        })
      }
    } catch (error) {
      console.error("Error loading item details:", error)
      toast({
        title: "Error",
        description: "Error al cargar los detalles del elemento",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    let processedValue = value

    if (type === "number") {
      processedValue = value === "" ? 0 : Number(value)
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }))

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }))
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.name.trim()) {
      errors.name = "El nombre es requerido"
    }

    if (!formData.sku.trim()) {
      errors.sku = "El SKU es requerido"
    }

    if (formData.current_stock < 0) {
      errors.current_stock = "El stock no puede ser negativo"
    }

    if (formData.min_stock_threshold < 0) {
      errors.min_stock_threshold = "El umbral mínimo de stock no puede ser negativo"
    }

    if (formData.max_stock_threshold < formData.min_stock_threshold) {
      errors.max_stock_threshold = "El umbral máximo de stock debe ser mayor que el umbral mínimo"
    }

    if (formData.common_price < 0) {
      errors.common_price = "El precio unitario no puede ser negativo"
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleGenerateSKU = async () => {
      if (!formData.name) {
        toast({
        title: "Información faltante",
        description: "Por favor ingresa un nombre para generar el SKU",
          variant: "destructive"
      })
      return
      }

    try {
      setGenerateSKULoading(true)
      const response = await generateSKU(clientId, formData.name)

      if (response.success && response.data.sku) {
        setFormData(prev => ({
          ...prev,
          sku: response.data.sku
        }))

        toast({
          title: "SKU Generado",
          description: `Nuevo SKU: ${response.data.sku}`,
          variant: "default"
        })
      }
    } catch (error) {
      console.error("Error generating SKU:", error)
      toast({
        title: "Error",
        description: "Error al generar SKU",
        variant: "destructive"
      })
    } finally {
      setGenerateSKULoading(false)
    }
  }

  const handleSave = async () => {
    if (!validateForm()) {
      toast({
        title: "Error de Validación",
        description: "Por favor revisa el formulario en busca de errores",
        variant: "destructive"
      })
      return
    }

    try {
      setSaving(true)
      let response

      if (mode === "create") {
        response = await createInventoryItem(clientId, formData)
        if (response.success) {
          toast({
            title: "Elemento Creado",
            description: "El elemento de inventario ha sido creado exitosamente",
            variant: "default"
          })
          onSave()
          onClose()
        }
      } else {
        response = await updateInventoryItem(item.id, formData)
        if (response.success) {
          toast({
            title: "Elemento Actualizado",
            description: "El elemento de inventario ha sido actualizado exitosamente",
            variant: "default"
          })
          onSave()
          onClose()
        }
      }

      if (!response.success) {
        throw new Error(response.message || "Error desconocido")
        }

    } catch (error) {
      console.error("Error saving item:", error)
      toast({
        title: "Error",
        description: error.message || "Error al guardar el elemento",
        variant: "destructive"
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      setDeleting(true)
      const response = await deleteInventoryItem(item.id)

      if (response.success) {
        toast({
          title: "Elemento Eliminado",
          description: "El elemento de inventario ha sido eliminado exitosamente",
          variant: "default"
        })
        onSave()
        onClose()
      } else {
        throw new Error(response.message || "Error al eliminar")
      }
    } catch (error) {
      console.error("Error deleting item:", error)
      toast({
        title: "Error",
        description: error.message || "Error al eliminar el elemento",
        variant: "destructive"
      })
    } finally {
      setDeleting(false)
      setShowDeleteDialog(false)
    }
  }

  const getStockStatus = () => {
    if (formData.current_stock <= 0) {
      return { label: "Sin Stock", icon: XCircle, color: "text-red-600 bg-red-50 dark:bg-red-900/20" }
    }
    if (formData.current_stock <= formData.min_stock_threshold) {
      return { label: "Poco Stock", icon: AlertTriangle, color: "text-amber-600 bg-amber-50 dark:bg-amber-900/20" }
    }
    return { label: "En Stock", icon: CheckCircle2, color: "text-green-600 bg-green-50 dark:bg-green-900/20" }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-slate-800 w-full max-w-4xl max-h-[90vh] overflow-hidden rounded shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded">
                <Package className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {mode === "create"
                    ? "Nuevo Elemento de Inventario"
                    : formData.name || "Detalles del Elemento"}
            </h2>
                {mode !== "create" && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    ID: {item?.id} • SKU: {formData.sku || "Sin SKU"}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {mode === "view" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsReadOnly(false)
                    onEditMode()
                  }}
                  className="rounded border-slate-300 dark:border-slate-600"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Editar
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="rounded"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center p-12">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 text-orange-500 animate-spin mb-4 mx-auto" />
                  <p className="text-slate-600 dark:text-slate-400">
                    Cargando detalles del elemento...
                  </p>
              </div>
            </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                {/* Clean UI Tabs */}
                <div className="border-b border-slate-200 dark:border-slate-700 px-6">
                  <TabsList className="grid w-full grid-cols-3 bg-slate-100 dark:bg-slate-700 rounded">
                    <TabsTrigger value="general" className="flex items-center rounded">
                  <Package className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">General</span>
                </TabsTrigger>
                    <TabsTrigger value="stock" className="flex items-center rounded">
                  <Archive className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">Stock y Precios</span>
                </TabsTrigger>
                    <TabsTrigger value="details" className="flex items-center rounded">
                  <FileText className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">Detalles Adicionales</span>
                </TabsTrigger>
              </TabsList>
                </div>

                <div className="flex-1 overflow-y-auto">
              {/* General Tab */}
                  <TabsContent value="general" className="p-6 space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
                        <Package className="h-4 w-4 mr-2 text-slate-400" />
                          Nombre del Elemento
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`rounded border-slate-300 dark:border-slate-600 ${
                            validationErrors.name ? "border-red-500" : ""
                          }`}
                          placeholder="Ingresa el nombre del elemento"
                          readOnly={isReadOnly}
                        />
                        {validationErrors.name && (
                          <p className="text-sm text-red-600">{validationErrors.name}</p>
                        )}
                    </div>

                    {/* SKU */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
                        <Tag className="h-4 w-4 mr-2 text-slate-400" />
                          SKU (Unidad de Mantenimiento de Stock)
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                        <div className="flex">
                        <Input
                          name="sku"
                          value={formData.sku}
                          onChange={handleInputChange}
                            className={`rounded-r-none border-slate-300 dark:border-slate-600 ${
                            validationErrors.sku ? "border-red-500" : ""
                          } ${!isReadOnly && "rounded-r-none"}`}
                            placeholder="Ingresa SKU"
                          readOnly={isReadOnly}
                        />
                        {!isReadOnly && (
                          <Button
                            type="button"
                            variant="outline"
                              className="rounded-l-none border-l-0 border-slate-300 dark:border-slate-600"
                            onClick={handleGenerateSKU}
                              disabled={generateSKULoading}
                          >
                              {generateSKULoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Tag className="h-4 w-4 mr-1" />
                                  Generar
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                      {validationErrors.sku && (
                          <p className="text-sm text-red-600">{validationErrors.sku}</p>
                      )}
                    </div>

                    {/* Description */}
                      <div className="lg:col-span-2 space-y-2">
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
                        <FileText className="h-4 w-4 mr-2 text-slate-400" />
                          Descripción
                      </Label>
                      <Textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                          className="bg-white dark:bg-slate-800 min-h-[100px] rounded border-slate-300 dark:border-slate-600"
                          placeholder="Ingresa la descripción del elemento"
                        readOnly={isReadOnly}
                      />
                  </div>

                    {/* Category */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Categoría
                      </Label>
                      {isReadOnly ? (
                          <div className="p-3 border border-slate-300 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                          {categories?.find(
                            (c) => c.id === formData.category_id
                            )?.name || "Sin categoría"}
                        </div>
                      ) : (
                        <Select
                            value={formData.category_id?.toString() || "0"}
                          onValueChange={(value) =>
                              setFormData(prev => ({ ...prev, category_id: Number(value) }))
                          }
                        >
                            <SelectTrigger className="rounded border-slate-300 dark:border-slate-600">
                              <SelectValue placeholder="Selecciona una categoría" />
                          </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">Sin categoría</SelectItem>
                            {categories?.map((category) => (
                                <SelectItem key={category.id} value={category.id.toString()}>
                                {category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                      {/* Subcategory */}
                      {subcategories.length > 0 && (
                      <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Subcategoría
                        </Label>
                        {isReadOnly ? (
                            <div className="p-3 border border-slate-300 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                            {subcategories?.find(
                              (s) => s.id === formData.subcategory_id
                              )?.name || "Ninguna"}
                          </div>
                        ) : (
                          <Select
                              value={formData.subcategory_id?.toString() || ""}
                            onValueChange={(value) =>
                                setFormData(prev => ({ ...prev, subcategory_id: value === "none" ? null : Number(value) }))
                            }
                          >
                              <SelectTrigger className="rounded border-slate-300 dark:border-slate-600">
                                <SelectValue placeholder="Selecciona una subcategoría" />
                            </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Ninguna</SelectItem>
                              {subcategories?.map((subcategory) => (
                                <SelectItem
                                  key={subcategory.id}
                                    value={subcategory.id.toString()}
                                >
                                  {subcategory.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                </div>
              </TabsContent>

              {/* Stock & Pricing Tab */}
                  <TabsContent value="stock" className="p-6 space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Current Stock */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
                        <Archive className="h-4 w-4 mr-2 text-slate-400" />
                          Stock Actual
                      </Label>
                      <div className="flex">
                        <Input
                            name="current_stock"
                          type="number"
                            value={formData.current_stock}
                          onChange={handleInputChange}
                            className={`rounded-r-none border-slate-300 dark:border-slate-600 ${
                              validationErrors.current_stock ? "border-red-500" : ""
                          }`}
                            min="0"
                          readOnly={isReadOnly}
                        />
                          {isReadOnly ? (
                            <div className="px-3 py-2 border border-l-0 border-slate-300 dark:border-slate-600 rounded-l-none bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                              {units.find(u => u.value === formData.unit)?.label || formData.unit}
                            </div>
                          ) : (
                        <Select
                              value={formData.unit}
                          onValueChange={(value) =>
                                setFormData(prev => ({ ...prev, unit: value }))
                          }
                        >
                              <SelectTrigger className="rounded-l-none w-[120px] border-l-0 border-slate-300 dark:border-slate-600">
                                <SelectValue placeholder="Selecciona unidad" />
                          </SelectTrigger>
                              <SelectContent>
                            <SelectItem value="unit_none">
                                  Selecciona unidad
                            </SelectItem>
                            {units.map((unit) => (
                                  <SelectItem key={unit.value} value={unit.value}>
                                    {unit.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                          )}
                      </div>
                        {validationErrors.current_stock && (
                          <p className="text-sm text-red-600">{validationErrors.current_stock}</p>
                      )}
                    </div>

                    {/* Min Stock Threshold */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
                        <AlertTriangle className="h-4 w-4 mr-2 text-slate-400" />
                          Umbral Mínimo de Stock
                      </Label>
                      <Input
                        name="min_stock_threshold"
                        type="number"
                        value={formData.min_stock_threshold}
                        onChange={handleInputChange}
                          className={`rounded border-slate-300 dark:border-slate-600 ${
                            validationErrors.min_stock_threshold ? "border-red-500" : ""
                        }`}
                          min="0"
                        readOnly={isReadOnly}
                      />
                      {validationErrors.min_stock_threshold && (
                          <p className="text-sm text-red-600">{validationErrors.min_stock_threshold}</p>
                      )}
                      <p className="text-xs text-slate-500">
                          Se mostrará una alerta cuando el stock caiga por debajo de este nivel
                      </p>
                    </div>

                    {/* Max Stock Threshold */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
                        <BarChart4 className="h-4 w-4 mr-2 text-slate-400" />
                          Umbral Máximo de Stock
                      </Label>
                      <Input
                        name="max_stock_threshold"
                        type="number"
                        value={formData.max_stock_threshold}
                        onChange={handleInputChange}
                          className={`rounded border-slate-300 dark:border-slate-600 ${
                            validationErrors.max_stock_threshold ? "border-red-500" : ""
                        }`}
                          min="0"
                        readOnly={isReadOnly}
                      />
                      {validationErrors.max_stock_threshold && (
                          <p className="text-sm text-red-600">{validationErrors.max_stock_threshold}</p>
                      )}
                  </div>

                    {/* Unit Price */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
                        <DollarSign className="h-4 w-4 mr-2 text-slate-400" />
                          Precio Unitario
                      </Label>
                      <div className="flex">
                        <Input
                          name="common_price"
                          type="number"
                          value={formData.common_price}
                          onChange={handleInputChange}
                            className={`rounded-r-none border-slate-300 dark:border-slate-600 ${
                              validationErrors.common_price ? "border-red-500" : ""
                          }`}
                            min="0"
                            step="0.01"
                          readOnly={isReadOnly}
                        />
                          {isReadOnly ? (
                            <div className="px-3 py-2 border border-l-0 border-slate-300 dark:border-slate-600 rounded-l-none bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                              {formData.currency}
                            </div>
                          ) : (
                        <Select
                              value={formData.currency}
                          onValueChange={(value) =>
                                setFormData(prev => ({ ...prev, currency: value }))
                          }
                        >
                              <SelectTrigger className="rounded-l-none w-[100px] border-l-0 border-slate-300 dark:border-slate-600">
                                <SelectValue placeholder="Moneda" />
                          </SelectTrigger>
                              <SelectContent>
                            {currencies.map((currency) => (
                                  <SelectItem key={currency.value} value={currency.value}>
                                    {currency.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                          )}
                      </div>
                      {validationErrors.common_price && (
                          <p className="text-sm text-red-600">{validationErrors.common_price}</p>
                      )}
                    </div>

                      {/* Stock Status Display */}
                      <div className="lg:col-span-2">
                        <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded border border-slate-200 dark:border-slate-700">
                          <h4 className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-3">
                            Estado del Stock
                        </h4>
                          {(() => {
                            const status = getStockStatus()
                            const IconComponent = status.icon
                            return (
                              <div className={`flex items-center p-3 rounded ${status.color}`}>
                                <IconComponent className="h-5 w-5 mr-3" />
                                <div>
                                  <div className="font-medium">{status.label}</div>
                                  <div className="text-sm opacity-75">
                                    {formData.current_stock} de {formData.max_stock_threshold} unidades
                        </div>
                      </div>
                              </div>
                            )
                          })()}
                        </div>
                  </div>
                </div>
              </TabsContent>

              {/* Additional Details Tab */}
                  <TabsContent value="details" className="p-6 space-y-6">
                    <div className="space-y-6">
                  {/* Notes */}
                  <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
                      <FileText className="h-4 w-4 mr-2 text-slate-400" />
                          Notas Adicionales
                    </Label>
                    <Textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                          className="rounded border-slate-300 dark:border-slate-600 min-h-[150px]"
                          placeholder="Ingresa cualquier nota adicional o información sobre este elemento"
                      readOnly={isReadOnly}
                    />
                  </div>
                </div>
              </TabsContent>
                </div>
            </Tabs>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200 dark:border-slate-700 p-6 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex flex-col sm:flex-row justify-between gap-3">
              <div className="flex gap-3">
                {mode !== "create" && !isReadOnly && (
                <Button
                  variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20 rounded"
                  onClick={() => setShowDeleteDialog(true)}
                    disabled={deleting}
                >
                  <Trash className="h-4 w-4 mr-2" />
                    Eliminar
                </Button>
              )}
            </div>

              <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                  className="rounded border-slate-300 dark:border-slate-600"
              >
                  Cancelar
              </Button>

                {!isReadOnly && (
                <Button
                  onClick={handleSave}
                    disabled={saving}
                    className="bg-orange-500 hover:bg-orange-600 text-white rounded"
                >
                    {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Guardar...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                        Guardar
                    </>
                  )}
                </Button>
              )}
              </div>
            </div>
          </div>
        </motion.div>
        </motion.div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="rounded border-slate-300 dark:border-slate-600">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-slate-900 dark:text-slate-100">
              ¿Eliminar Elemento?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
              Esta acción no se puede deshacer. El elemento será eliminado permanentemente
              de tu inventario.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
            <AlertDialogCancel className="rounded border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
              Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white rounded"
              >
              {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminar...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                  Eliminar
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </AnimatePresence>
  )
}

